import type { BrandAliasLookup } from '@/state';
import {
    brandRequestIdFor,
    followLiveBrand,
    isCatalogableBrandName,
    type SharedNameplateTable,
    sharedNameplateForKey,
} from '@rock/brands';
import type { Brand, BrandAlias, BrandId, BrandLine, SourcedValue } from '@rock/types';
import { normalizeBrandName } from '@rock/util';

/**
 * Where one extracted brand name stands with the catalog:
 *  - `gating`: resolves to a catalog brand that is in the recall's brand gate — it limits matching.
 *  - `resolved`: resolves to a catalog brand the gate does not hold yet (minted after processing, or
 *    removed by an admin) — one click adds it; Approve re-resolves it anyway.
 *  - `requested`: not in the catalog; processing recorded a sighting on `brand-requests/{key}`, where a
 *    human decides what the name is — a candidate, not a gate, until that request resolves.
 *  - `shared`: a shared nameplate ("Hunter") — two companies print it and a recall has no space to
 *    route by, so processing links nothing and requests nothing; a human requires the right side.
 *  - `unresolved`: never requested — a placeholder or an ambiguous word, or a key the catalog holds for a
 *    brand that is no longer live (junk, or a merge chain too long to follow).
 */
export type RecallBrandNameState = 'gating' | 'resolved' | 'requested' | 'shared' | 'unresolved';

export interface RecallBrandName {
    name: string;
    source: SourcedValue['source'];
    state: RecallBrandNameState;
    brand?: Brand;
    /** The line the name resolved through, when it is a line key ("Hunter Brunswick"). */
    line?: BrandLine;
    /** The queued request's id, for `requested` names. */
    requestId?: string;
    /** The nameplate's key and its qualified sides, for `shared` names. */
    shared?: { key: string; sides: Brand[] };
}

/** The folded keys a set of names resolves through — the argument to `brandAliasLookupState`. */
export function recallBrandNameKeys(names: SourcedValue[]): string[] {
    return names.map(({ value }) => normalizeBrandName(value).key);
}

/**
 * Classify the extractors' brand names the way processing resolved them: through the `brand-aliases`
 * projection (`aliases`, one doc per folded key), with a merged brand followed to its winner and a junk one
 * dropped — the same lookup and the same request rule as `resolveRecallBrands` server-side (a key the
 * catalog holds is never requested), so what the review shows is what processing did. The nameplate table
 * only settles a key with no doc or a denied one.
 */
export function classifyRecallBrandNames(
    names: SourcedValue[],
    brands: Brand[],
    gatingBrandIds: BrandId[],
    sharedNameplates: SharedNameplateTable,
    aliases: BrandAliasLookup,
): RecallBrandName[] {
    return names.map(({ value, source }) => {
        const key = normalizeBrandName(value).key;
        const doc = aliases.get(key);
        const resolved = doc ? resolvedBrand(doc, brands) : undefined;
        if (resolved) {
            const state = gatingBrandIds.includes(resolved.brand.id) ? 'gating' : 'resolved';
            return { name: value, source, ...resolved, state };
        }
        const nameplate = sharedNameplateForKey(doc?.nameplateKey ?? key, sharedNameplates);
        if (doc?.denied || nameplate) {
            const sides = (nameplate?.sides ?? []).flatMap((id) => brands.filter((brand) => brand.id === id));
            const sharedKey = nameplate?.key ?? doc?.nameplateKey ?? key;
            return { name: value, source, state: 'shared', shared: { key: sharedKey, sides } };
        }
        if (!doc && isCatalogableBrandName(value, sharedNameplates)) {
            return { name: value, source, state: 'requested', requestId: brandRequestIdFor(value) };
        }
        return { name: value, source, state: 'unresolved' };
    });
}

/** The brand an alias doc points at (`liveBrandIn`) and the line it names on it. */
function resolvedBrand(doc: BrandAlias, brands: Brand[]): { brand: Brand; line?: BrandLine } | undefined {
    const brand = doc.brandId ? liveBrandIn(doc.brandId, brands) : undefined;
    if (!brand) return undefined;
    const line = doc.lineId ? brand.lines?.find((candidate) => candidate.id === doc.lineId) : undefined;
    return { brand, ...(line ? { line } : {}) };
}

/** The brand an id names today, read from the loaded catalog by the server's rule (`followLiveBrand`). */
export function liveBrandIn(brandId: BrandId, brands: Brand[]): Brand | undefined {
    return followLiveBrand(brandId, (id) => brands.find((candidate) => candidate.id === id));
}
